import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './components/Login';
import {
  NavigationTracker,
  startAuditHeartbeat,
  stopAuditHeartbeat,
  attachBeaconLogout,
  detachBeaconLogout
} from './utils/auditClient';
import './App.css';

// Reload once when a lazy chunk fails to load. Tabs opened before a deploy
// hold a manifest referencing old hashed chunks the new deployment no longer
// serves, so their next route change throws ChunkLoadError and blanks the
// page. The timestamp guard stops a reload loop if the chunk is genuinely
// broken; the never-resolving promise keeps Suspense showing its spinner
// (instead of a crash) while the reload happens.
const lazyWithReload = (factory) => lazy(() =>
  factory().catch((err) => {
    const KEY = 'chunk-reload-at';
    const last = Number(sessionStorage.getItem(KEY) || 0);
    if (Date.now() - last > 30000) {
      sessionStorage.setItem(KEY, String(Date.now()));
      window.location.reload();
      return new Promise(() => {});
    }
    throw err;
  })
);

const Dashboard = lazyWithReload(() => import('./components/Dashboard'));
const Screenshots = lazyWithReload(() => import('./components/Screenshots'));
const Users = lazyWithReload(() => import('./components/Users'));
const Analytics = lazyWithReload(() => import('./components/Analytics'));
const UserActivity = lazyWithReload(() => import('./components/UserActivity'));
const Teams = lazyWithReload(() => import('./components/Teams'));
const AttendanceLogs = lazyWithReload(() => import('./components/AttendanceLogs'));
const AdminActivity = lazyWithReload(() => import('./components/AdminActivity'));
const Profile = lazyWithReload(() => import('./components/Profile'));
const OrganizationsList = lazyWithReload(() => import('./components/superadmin/OrganizationsList'));
const OrganizationDetail = lazyWithReload(() => import('./components/superadmin/OrganizationDetail'));
const BootstrapPage = lazyWithReload(() => import('./components/BootstrapPage'));

const API_URL = process.env.REACT_APP_API_URL || '';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const verifyToken = useCallback(async (token) => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token verification failed:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');

      if (token) {
        const isValid = await verifyToken(token);
        if (!isValid) {
          // Token is invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [verifyToken]);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${API_URL}/api/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  // Audit-session lifecycle. While authenticated: heartbeat keeps the
  // admin_sessions row's last_seen_at fresh, beforeunload/pagehide beacon
  // closes the session if the tab is killed without an explicit logout.
  useEffect(() => {
    if (!isAuthenticated) return;
    startAuditHeartbeat();
    attachBeaconLogout();
    return () => {
      stopAuditHeartbeat();
      detachBeaconLogout();
    };
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Super-admin lands on the org list; everyone else on the dashboard.
  const isSuperAdmin = isAuthenticated && user?.role === 'super_admin';
  const homePath = isSuperAdmin ? '/superadmin/organizations' : '/dashboard';

  return (
    <Router>
      <div className="App">
        {isAuthenticated && <NavigationTracker />}
        <Suspense fallback={<div className="loading-screen"><div className="spinner"></div></div>}>
        <Routes>
          <Route path="/bootstrap" element={<BootstrapPage />} />
          <Route
            path="/login"
            element={
              !isAuthenticated ?
              <Login onLogin={handleLogin} /> :
              <Navigate to={homePath} />
            }
          />
          <Route
            path="/dashboard"
            element={
              !isAuthenticated ? <Navigate to="/login" /> :
              isSuperAdmin ? <Navigate to="/superadmin/organizations" /> :
              <Dashboard user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/screenshots"
            element={
              !isAuthenticated ? <Navigate to="/login" /> :
              isSuperAdmin ? <Navigate to="/superadmin/organizations" /> :
              <Screenshots user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/users"
            element={
              isAuthenticated && user?.role === 'admin' ?
              <Users user={user} onLogout={handleLogout} /> :
              <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/attendance-logs"
            element={
              !isAuthenticated ? <Navigate to="/login" /> :
              isSuperAdmin ? <Navigate to="/superadmin/organizations" /> :
              <AttendanceLogs user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/analytics"
            element={
              !isAuthenticated ? <Navigate to="/login" /> :
              isSuperAdmin ? <Navigate to="/superadmin/organizations" /> :
              <Analytics user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/user-activity"
            element={
              isAuthenticated && (user?.role === 'admin' || user?.role === 'team_manager') ?
              <UserActivity user={user} onLogout={handleLogout} /> :
              <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/teams"
            element={
              isAuthenticated && (user?.role === 'admin' || user?.role === 'team_manager') ?
              <Teams user={user} onLogout={handleLogout} /> :
              <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/admin-activity"
            element={
              isAuthenticated && user?.role === 'admin' ?
              <AdminActivity user={user} onLogout={handleLogout} /> :
              <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/profile"
            element={
              isAuthenticated ?
              <Profile user={user} onLogout={handleLogout} /> :
              <Navigate to="/login" />
            }
          />
          <Route
            path="/superadmin/organizations"
            element={
              isSuperAdmin ?
              <OrganizationsList user={user} onLogout={handleLogout} /> :
              <Navigate to={isAuthenticated ? "/dashboard" : "/login"} />
            }
          />
          <Route
            path="/superadmin/organizations/:id"
            element={
              isSuperAdmin ?
              <OrganizationDetail user={user} onLogout={handleLogout} /> :
              <Navigate to={isAuthenticated ? "/dashboard" : "/login"} />
            }
          />
          <Route
            path="/"
            element={<Navigate to={isAuthenticated ? homePath : "/login"} />}
          />
        </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;
